import { useState, useEffect, useRef, useCallback } from 'react';
import {
  IonContent,
  IonHeader,
  IonPage,
  IonTitle,
  IonToolbar,
  IonRefresher,
  IonRefresherContent,
  RefresherEventDetail,
  useIonToast,
  IonButtons,
  IonIcon,
} from '@ionic/react';
import { notificationsOutline } from 'ionicons/icons';
import { Box, Badge, Dialog, IconButton, Button, Typography } from '@mui/material';
import Notifications from './Notifications';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { collection, getDocs, query, where, doc, getDoc, onSnapshot } from 'firebase/firestore';
import { auth, db } from '../firebase/config';
import { SearchBar } from '../components/SearchBar';
import { PostCard } from '../components/PostCard';
import { Post, SearchFilters } from '../types/post';
import { BaseUser } from '../types/user';

const getUserName = (user: any): string => {
  if (!user) return 'Unknown User';
  try {
    switch (user.type) {
      case 'company':
        return user.companyName || 'Company';
      case 'contractor':
        if (user.firstName && user.lastName) {
          return `${user.lastName.charAt(0)}. ${user.firstName}`;
        }
        return 'Unknown Contractor';
      case 'ambassador':
        return user.username || `${user.firstName} ${user.lastName}` || 'Unknown Ambassador';
      default:
        return 'Unknown User';
    }
  } catch (error) {
    console.error('Error getting user name:', error);
    return 'Unknown User';
  }
};

const Home: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const [currentUser, setCurrentUser] = useState<BaseUser | null>(null);
  const [posts, setPosts] = useState<Post[]>([]);
  const [filters, setFilters] = useState<SearchFilters>({});
  const [presentToast] = useIonToast();
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [displayCount, setDisplayCount] = useState(30);

  // Listen for unread notifications
  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;

    const notificationsQuery = query(
      collection(db, 'notifications'),
      where('toUserId', '==', user.uid),
      where('read', '==', false)
    );

    const unsubscribe = onSnapshot(notificationsQuery, (snapshot) => {
      // Filter out notifications where the user is the sender
      const filteredNotifications = snapshot.docs.filter(doc => {
        const data = doc.data();
        return data.fromUserId !== user.uid;
      });
      setUnreadNotifications(filteredNotifications.length);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        if (!user.emailVerified) {
          history.replace('/email-verification');
          return;
        }
        // Fetch current user data
        const userDoc = await getDocs(query(
          collection(db, 'users'),
          where('id', '==', user.uid)
        ));
        
        if (!userDoc.empty) {
          setCurrentUser(userDoc.docs[0].data() as BaseUser);
        }
      }
    });

    return () => unsubscribe();
  }, [history]);

  useEffect(() => {
    if (currentUser) {
      const unsubscribe = listenForPosts();
      return () => unsubscribe();
    }
  }, [currentUser, filters]);

  const listenForPosts = () => {
    setIsLoading(true);
    // Create a listener for posts collection
    const postsRef = collection(db, 'posts');
    return onSnapshot(postsRef, async (postsSnapshot) => {
      try {
        // Fetch all users for filtering
        const usersRef = collection(db, 'users');
        const usersSnapshot = await getDocs(usersRef);
        const usersMap = new Map();
        
        usersSnapshot.docs.forEach(doc => {
          const userData = doc.data();
          usersMap.set(doc.id, userData);
        });
        
        // Convert posts to final format
        const allPosts = postsSnapshot.docs.map(doc => {
          const data = doc.data();
          const userId = data.authorId || data.userId;
          const userData = usersMap.get(userId);
          
          return {
            id: doc.id,
            ...data,
            // Ensure all required fields are present
            authorId: userId,
            authorName: data.authorName || (userData ? getUserName(userData) : 'Unknown User'),
            authorPhotoURL: data.authorPhotoURL || (userData?.profilePhotoUrl || ''),
            createdAt: data.createdAt?.toDate() || new Date(),
            updatedAt: data.updatedAt?.toDate() || new Date(),
            likedBy: data.likedBy || [],
            likes: data.likes || 0,
            comments: data.comments || []
          } as Post;
        });

        // Apply filters
        let filteredPosts = [...allPosts];
        
        // Apply filters (same as in fetchPosts)
        // Apply name search filter
        if (filters.name) {
          const searchTerm = filters.name.toLowerCase();
          filteredPosts = filteredPosts.filter(post => {
            const postUser = usersMap.get(post.authorId);
            if (!postUser) return false;
            
            const userName = getUserName(postUser).toLowerCase();
            return userName.includes(searchTerm);
          });
        }
        
        // Apply activity type filter
        if (filters.activityType) {
          filteredPosts = filteredPosts.filter(post => {
            const postUser = usersMap.get(post.authorId);
            if (!postUser) return false;
            
            const userActivity = getActivityValue(postUser).toLowerCase();
            return userActivity === filters.activityType?.toLowerCase();
          });
        }
        
        // Apply location filters
        if (filters.proximity) {
          filteredPosts = await Promise.all(
            filteredPosts.map(async post => {
              const postUser = usersMap.get(post.authorId);
              console.log('Post user location:', postUser.city, postUser.country);
              console.log('Current user location:', currentUser.city, currentUser.country);
              if (!postUser) return null;
              
              if (filters.proximity === 'same_city' && postUser.city !== currentUser?.city) {
                return null;
              }
              
              if (filters.proximity === 'other_countries' && postUser.country === currentUser?.country) {
                return null;
              }
              
              if (filters.proximity === 'other_cities' && currentUser) {
                if (postUser.city === currentUser.city) return null;
                
                if (filters.kmRange) {
                  // Calculate distance between user and post author
                  const distance = await getDistanceBetweenLocations(
                    currentUser.city,
                    currentUser.country,
                    postUser.city,
                    postUser.country
                  );
                  
                  // If we couldn't calculate the distance or it's greater than the filter range, exclude the post
                  if (!distance || distance > parseInt(filters.kmRange)) {
                    return null;
                  }
                }
              }
              
              return post;
            })
          );
          
          // Filter out null values
          filteredPosts = filteredPosts.filter(post => post !== null) as Post[];
        }
        
        // Apply sort order
        if (filters.sortOrder === 'oldest_first') {
          filteredPosts.sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());
        } else {
          // Default: newest first
          filteredPosts.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
        }
        
        setPosts(filteredPosts);
        setIsLoading(false);
      } catch (error) {
        presentToast({
          message: t('home.fetch_error'),
          duration: 2000,
          color: 'danger'
        });
        setIsLoading(false);
      }
    });
  };

  const fetchPosts = async () => {
    setIsLoading(true);
    try {
      // Fetch all posts
      const postsRef = collection(db, 'posts');
      const postsSnapshot = await getDocs(postsRef);
      
      // Fetch all users for filtering
      const usersRef = collection(db, 'users');
      const usersSnapshot = await getDocs(usersRef);
      const usersMap = new Map();
      
      usersSnapshot.docs.forEach(doc => {
        const userData = doc.data();
        usersMap.set(doc.id, userData);
      });
      
      // Convert posts to final format
      const allPosts = postsSnapshot.docs.map(doc => {
        const data = doc.data();
        const userId = data.authorId || data.userId;
        const userData = usersMap.get(userId);
        
        return {
          id: doc.id,
          ...data,
          // Ensure all required fields are present
          authorId: userId,
          authorName: data.authorName || (userData ? getUserName(userData) : 'Unknown User'),
          authorPhotoURL: data.authorPhotoURL || (userData?.profilePhotoUrl || ''),
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
          likedBy: data.likedBy || [],
          likes: data.likes || 0,
          comments: data.comments || []
        } as Post;
      });

      // Apply filters
      let filteredPosts = [...allPosts];
      
      // Apply name search filter
      if (filters.name) {
        const searchTerm = filters.name.toLowerCase();
        filteredPosts = filteredPosts.filter(post => {
          const postUser = usersMap.get(post.authorId);
          if (!postUser) return false;
          
          const userName = getUserName(postUser).toLowerCase();
          return userName.includes(searchTerm);
        });
      }
      
      // Apply activity type filter
      if (filters.activityType) {
        filteredPosts = filteredPosts.filter(post => {
          const postUser = usersMap.get(post.authorId);
          if (!postUser) return false;
          
          const userActivity = getActivityValue(postUser).toLowerCase();
          return userActivity === filters.activityType?.toLowerCase();
        });
      }
      
      // Apply location filters
      if (filters.proximity) {
        filteredPosts = await Promise.all(
          filteredPosts.map(async post => {
            const postUser = usersMap.get(post.authorId);
            if (!postUser) return null;
            
            if (filters.proximity === 'same_city' && postUser.city !== currentUser?.city) {
              return null;
            }
            
            if (filters.proximity === 'other_countries' && postUser.country === currentUser?.country) {
              return null;
            }
            
            if (filters.proximity === 'other_cities' && currentUser) {
              if (postUser.city === currentUser.city) return null;
              
              if (filters.kmRange) {
                // Calculate distance between user and post author
                const distance = await getDistanceBetweenLocations(
                  currentUser.city,
                  currentUser.country,
                  postUser.city,
                  postUser.country
                );
                
                // If we couldn't calculate the distance or it's greater than the filter range, exclude the post
                if (!distance || distance > parseInt(filters.kmRange)) {
                  return null;
                }
              }
            }
            
            return post;
          })
        );
        
        // Filter out null values
        filteredPosts = filteredPosts.filter(post => post !== null) as Post[];
      }
      
      // Apply sort order
      if (filters.sortOrder === 'oldest_first') {
        filteredPosts.sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());
      } else {
        // Default: newest first
        filteredPosts.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
      }
      
      setPosts(filteredPosts);
      setIsLoading(false);
    } catch (error) {
      presentToast({
        message: t('home.fetch_error'),
        duration: 2000,
        color: 'danger'
      });
      setIsLoading(false);
    }
  };

  const handleRefresh = async (event: CustomEvent<RefresherEventDetail>) => {
    try {
      // The posts will be automatically refreshed by the listener
      // Just complete the refresh event
      event.detail.complete();
    } catch (error) {
      event.detail.complete();
      presentToast({
        message: t('home.refresh_error'),
        duration: 2000,
        color: 'danger'
      });
    }
  };

  const getCustomFieldValue = (field?: { value: string; isOther: boolean; customValue: string | null }) => {
    if (!field) return '';
    return field.isOther ? field.customValue || '' : field.value;
  };

  const getActivityValue = (user: any): string => {
    if (!user) return '';
    try {
      switch (user.type) {
        case 'company':
          return getCustomFieldValue(user.industry) || '';
        case 'contractor':
          return getCustomFieldValue(user.serviceType) || '';
        case 'ambassador': {
          return getCustomFieldValue(user.activityType);
        }
        default:
          return '';
      }
    } catch (error) {
      console.error('Error getting activity value:', error);
      return '';
    }
  };

  // Cache for coordinates to avoid repeated API calls
  const coordinatesCache = useRef<Map<string, [number, number] | null>>(new Map());
  
  // Track pending requests to avoid duplicate API calls for the same location
  const pendingRequests = useRef<Map<string, Promise<[number, number] | null>>>(new Map());
  
  // Rate limiting - Nominatim allows 1 request per second
  const lastRequestTime = useRef<number>(0);
  const requestQueue = useRef<Array<() => void>>([]);
  
  // Process the request queue
  const processQueue = useCallback(() => {
    if (requestQueue.current.length === 0) return;
    
    const now = Date.now();
    const timeElapsed = now - lastRequestTime.current;
    
    // If at least 1 second has passed since the last request
    if (timeElapsed >= 1000) {
      const nextRequest = requestQueue.current.shift();
      if (nextRequest) {
        lastRequestTime.current = now;
        nextRequest();
        
        // Schedule next queue processing
        setTimeout(processQueue, 1000);
      }
    } else {
      // Schedule next queue processing after waiting the remaining time
      setTimeout(processQueue, 1000 - timeElapsed);
    }
  }, []);
  
  // Function to make a rate-limited request
  const makeRateLimitedRequest = useCallback(<T,>(requestFn: () => Promise<T>): Promise<T> => {
    return new Promise((resolve) => {
      const executeRequest = async () => {
        try {
          const result = await requestFn();
          resolve(result);
        } catch (error) {
          console.error('Rate-limited request failed:', error);
          resolve(null as unknown as T);
        }
      };
      
      requestQueue.current.push(executeRequest);
      
      // Start processing the queue if it's not already running
      if (requestQueue.current.length === 1 && Date.now() - lastRequestTime.current >= 1000) {
        processQueue();
      }
    });
  }, [processQueue]);

  const getCoordinates = useCallback(async (city: string, country: string): Promise<[number, number] | null> => {
    if (!city || !country) {
      console.warn('Missing city or country for coordinate lookup');
      return null;
    }
    
    // Create a cache key from city and country
    const cacheKey = `${city.toLowerCase()},${country.toLowerCase()}`;
    
    // Check if we already have these coordinates in cache
    if (coordinatesCache.current.has(cacheKey)) {
      return coordinatesCache.current.get(cacheKey);
    }
    
    // Check if there's already a pending request for this location
    if (pendingRequests.current.has(cacheKey)) {
      return pendingRequests.current.get(cacheKey);
    }
    
    // Create a new request and store it in pendingRequests
    const requestPromise = makeRateLimitedRequest(async () => {
      try {
        // Add proper headers as per Nominatim usage policy
        // https://operations.osmfoundation.org/policies/nominatim/
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
            `${city}, ${country}`
          )}&limit=1&addressdetails=1`,
          {
            headers: {
              'User-Agent': 'WiseLinker App (https://wiselinker.com)',
              'Referer': window.location.origin
            }
          }
        );
        
        if (!response.ok) {
          throw new Error(`Nominatim API error: ${response.status} ${response.statusText}`);
        }
        
        const data = await response.json();
        
        let result: [number, number] | null = null;
        if (data && data.length > 0) {
          result = [parseFloat(data[0].lat), parseFloat(data[0].lon)];
        } else {
          console.warn(`No coordinates found for ${city}, ${country}`);
        }
        
        // Store in cache
        coordinatesCache.current.set(cacheKey, result);
        return result;
      } catch (error) {
        console.error(`Error getting coordinates for ${city}, ${country}:`, error);
        // Cache the failure too to avoid repeated failed requests
        coordinatesCache.current.set(cacheKey, null);
        return null;
      } finally {
        // Remove from pending requests
        pendingRequests.current.delete(cacheKey);
      }
    });
    
    // Store the promise in pendingRequests
    pendingRequests.current.set(cacheKey, requestPromise);
    return requestPromise;
  }, [makeRateLimitedRequest]);

  // Calculate distance between two points using the Haversine formula
  const calculateDistance = useCallback((lat1: number, lon1: number, lat2: number, lon2: number): number => {
    // Haversine formula for calculating distance between two points on a sphere
    const R = 6371; // Radius of the earth in km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c; // Distance in km
  }, []);
  
  // Helper function to get distance between two locations
  const getDistanceBetweenLocations = useCallback(async (
    city1: string, 
    country1: string, 
    city2: string, 
    country2: string
  ): Promise<number | null> => {
    try {
      // Get coordinates for both locations
      const [coords1, coords2] = await Promise.all([
        getCoordinates(city1, country1),
        getCoordinates(city2, country2)
      ]);
      
      // If either set of coordinates is missing, we can't calculate distance
      if (!coords1 || !coords2) {
        return null;
      }
      
      // Calculate distance
      return calculateDistance(coords1[0], coords1[1], coords2[0], coords2[1]);
    } catch (error) {
      console.error('Error calculating distance between locations:', error);
      return null;
    }
  }, [getCoordinates, calculateDistance]);

  const handleSearch = async (newFilters: SearchFilters) => {
    setFilters(newFilters);
    
    try {
      // Fetch all users for filtering
      const usersRef = collection(db, 'users');
      const usersSnapshot = await getDocs(usersRef);
      const usersMap = new Map();
      
      usersSnapshot.docs.forEach(doc => {
        const userData = doc.data();
        usersMap.set(doc.id, userData);
      });

      // Filter posts based on users and search criteria
      const filterPost = async (post: Post, postUser: any) => {
        // Name search
        if (newFilters.name) {
          const searchTerm = newFilters.name.toLowerCase();
          const userName = getUserName(postUser).toLowerCase();
          if (!userName.includes(searchTerm)) {
            return false;
          }
        }

        // Activity type filter
        if (newFilters.activityType) {
          const userActivity = getActivityValue(postUser);
          if (userActivity.toLowerCase() !== newFilters.activityType.toLowerCase()) {
            return false;
          }
        }

        // Location filters
        if (newFilters.proximity) {
          if (newFilters.proximity === 'same_city' && postUser.city !== currentUser?.city) {
            return false;
          }
          if (newFilters.proximity === 'other_countries' && postUser.country === currentUser?.country) {
            return false;
          }
          if (newFilters.proximity === 'other_cities' && currentUser) {
            if (postUser.city === currentUser.city) return false;
            
            if (newFilters.kmRange) {
              // Calculate distance between user and post author
              const distance = await getDistanceBetweenLocations(
                currentUser.city,
                currentUser.country,
                postUser.city,
                postUser.country
              );
              
              // If we couldn't calculate the distance or it's greater than the filter range, exclude the post
              if (!distance || distance > parseInt(newFilters.kmRange)) {
                return false;
              }
            }
          }
        }

        return true;
      };

      const filteredPosts = [];
      for (const post of posts) {
        const postUser = usersMap.get(post.authorId);
        if (postUser && await filterPost(post, postUser)) {
          filteredPosts.push(post);
        }
      }

      setPosts(filteredPosts);
    } catch (error) {
      console.error('Error filtering posts:', error);
      presentToast({
        message: t('home.filter_error'),
        duration: 2000,
        color: 'danger'
      });
    }
  };

  return (
    <IonPage>
      <IonHeader className="ion-no-border">
        <IonToolbar className="glass-effect" style={{ 
          '--background': 'transparent',
          '--border-width': '0',
          '--padding-top': '10px',
          '--padding-bottom': '10px'
        }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            px: 2
          }}>
            <Typography 
              variant="h5" 
              sx={{ 
                fontWeight: 700,
                letterSpacing: '-0.5px',
                color: '#0068B9'
              }}
            >
              {t('home.feed_title')}
            </Typography>
            <IconButton 
              onClick={() => setNotificationsOpen(true)} 
              size="large"
              sx={{
                background: 'rgba(255, 255, 255, 0.8)',
                boxShadow: '0 4px 12px rgba(0, 104, 185, 0.1)',
                transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 16px rgba(0, 104, 185, 0.15)'
                }
              }}
            >
              <Badge 
                badgeContent={unreadNotifications} 
                color="primary" 
                max={99}
                sx={{
                  '& .MuiBadge-badge': {
                    fontSize: '0.7rem',
                    height: '18px',
                    minWidth: '18px',
                    top: -2,
                    right: -2
                  }
                }}
              >
                <IonIcon icon={notificationsOutline} style={{ fontSize: '22px', color: '#0068B9' }} />
              </Badge>
            </IconButton>
          </Box>
        </IonToolbar>
      </IonHeader>

      {/* Notifications Dialog */}
      <Dialog
        open={notificationsOpen}
        onClose={() => setNotificationsOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            height: '80vh',
            maxHeight: '600px',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }
        }}
      >
        <Notifications onClose={() => setNotificationsOpen(false)} />
      </Dialog>
      
      <IonContent>
        {/* Temporarily disabled IonRefresher due to initialization issues */}
        {/* <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
          <IonRefresherContent />
        </IonRefresher> */}

        <SearchBar onSearch={handleSearch} />

        <Box sx={{ p: 2, pb: 8, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {isLoading ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <div className="loading-spinner" style={{ margin: '0 auto' }}>
                <div className="spinner"></div>
              </div>
              <p style={{ marginTop: '16px', color: 'text.secondary' }}>{t('home.loading_posts')}</p>
            </Box>
          ) : (
            <>
              {posts.slice(0, displayCount).map(post => (
                <PostCard 
                  key={post.id} 
                  post={post} 
                  currentUserId={currentUser?.id}
                />
              ))}
              
              {posts.length > displayCount && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 4 }}>
                  <Button 
                    variant="outlined" 
                    onClick={() => setDisplayCount(prev => prev + 30)}
                  >
                    {t('home.show_more')}
                  </Button>
                </Box>
              )}
              
              {posts.length === 0 && (
                <Box sx={{ textAlign: 'center', color: 'text.secondary', py: 4 }}>
                  {filters.name || filters.activityType || filters.proximity ? (
                    <div>
                      <p>{t('home.no_search_results')}</p>
                      <p>{t('home.try_different_search')}</p>
                    </div>
                  ) : (
                    t('home.no_posts')
                  )}
                </Box>
              )}
            </>
          )}
        </Box>
      </IonContent>
    </IonPage>
  );
};

export default Home;
